// Localização: ./src/context/PermissionContext.js (Versão 2.1.0 - Implementação Completa)

import React, { createContext, useContext } from 'react';
import { useAuth } from './AuthContext';

const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
    const { user } = useAuth();
    
    /**
     * Verifica se o usuário tem permissão para uma ação específica em um recurso.
     * @param {string} resource - Nome do recurso/módulo (ex: 'pacientes', 'medicamentos')
     * @param {string} action - Ação a ser verificada (ex: 'can_read', 'can_create', 'can_edit', 'can_delete', 'can_access')
     * @returns {boolean} - true se o usuário tem permissão, false caso contrário
     */
    const checkPermission = (resource, action = 'can_access') => {
        if (!user) {
            return false;
        }

        // ✅ CORREÇÃO: Roles de alto privilégio (proprietário/admin) têm acesso total a todos os recursos
        const isHighPrivilege = user.role === 'proprietario' || user.role === 'admin';
        if (isHighPrivilege) {
            return true;
        }

        if (!user.permissions) {
            return false;
        }

        // Verifica se o recurso existe nas permissões do usuário
        const resourcePerms = user.permissions[resource];
        if (!resourcePerms) {
            return false;
        }

        // 🛑 CORREÇÃO: Normalizar valor (pode vir como boolean do backend)
        // Verifica se a ação específica está permitida (aceita 1, true, ou '1')
        const value = resourcePerms[action];
        return value === 1 || value === true || value === '1';
    };

    /**
     * Verifica se o usuário tem qualquer uma das ações especificadas
     * @param {string} resource - Nome do recurso
     * @param {string[]} actions - Array de ações a verificar
     * @returns {boolean}
     */
    const checkAnyPermission = (resource, actions) => {
        return actions.some(action => checkPermission(resource, action));
    };

    /**
     * Verifica se o usuário tem todas as ações especificadas
     * @param {string} resource - Nome do recurso
     * @param {string[]} actions - Array de ações a verificar
     * @returns {boolean}
     */
    const checkAllPermissions = (resource, actions) => {
        return actions.every(action => checkPermission(resource, action));
    };

    const value = {
        checkPermission,
        checkAnyPermission,
        checkAllPermissions,
        // Helpers para verificar roles específicas
        isHighPrivilege: user?.role === 'proprietario' || user?.role === 'admin',
        canManageUsers: checkPermission('usuarios', 'can_edit') || checkPermission('usuarios', 'can_create'),
        canManageConfig: checkPermission('config', 'can_edit'),
    };

    return (
        <PermissionContext.Provider value={value}>
            {children}
        </PermissionContext.Provider>
    );
};

export const usePermissions = () => {
    return useContext(PermissionContext);
};